/**
 * The typed reference to an extension point (surface extension points, D9, D12).
 *
 * `zion-app points pull <id>` writes one file per point: zod parsers for the point's input and
 * outputs, and one `defineExtensionPoint(id, { input, outputs })` call that ties them to the id. A
 * consumer passes the reference where the runtime takes a point id: `mountSurface(el, { point:
 * ref.id })`, or `<Embed point={ref}>` in the React binding, which reads the parsers to type the input
 * and the output payloads.
 *
 * The parsers are structural ({@link SchemaParser}), so this package takes no zod dependency (D12 of
 * the layered-shell-surfaces plan). The module holds its own copy of the id grammar for the same
 * reason; `extension-point-ref.test.ts` pins both values and asserts each equals the contract's
 * `EXTENSION_POINT_ID_PATTERN` and `EXTENSION_POINT_ID_MAX_LENGTH` in `@zion/sdk-contracts`.
 */
/**
 * The id grammar of an extension point, `<owner>/<name>.vN`: the runtime's copy of
 * `EXTENSION_POINT_ID_PATTERN` in `@zion/sdk-contracts`. Not exported from `./v1`.
 */
export const REF_ID_PATTERN = '^[a-z][a-z0-9-]*[a-z0-9]/[a-z][a-z0-9-]*\\.v[1-9][0-9]{0,3}$';
/** The longest extension point id, checked before {@link REF_ID_PATTERN}: the runtime's copy of `EXTENSION_POINT_ID_MAX_LENGTH`. Not exported from `./v1`. */
export const REF_ID_MAX_LENGTH = 96;
const REF_ID = new RegExp(REF_ID_PATTERN);
function isParser(value) {
    return typeof value === 'object' && value !== null && typeof value.safeParse === 'function';
}
/**
 * Make the typed reference to the extension point `id`.
 *
 * Inputs:
 * - `id`: a string of at most 96 characters that matches `<owner>/<name>.vN`, for example
 *   `admin-app/user-picker.v1`.
 * - `parsers`: optional. `input` is one parser; `outputs` is a plain object of parsers keyed by
 *   output name. Each parser is an object with a `safeParse` function.
 *
 * Output: a frozen {@link ExtensionPointRef} whose `id` is `id`. It carries the key `input` only
 * when `parsers.input` is given, and the key `outputs` only when `parsers.outputs` is given. The
 * `outputs` map is a frozen copy of the given object's own enumerable keys, so a later change to the
 * caller's object changes nothing. The parser objects are kept as given.
 *
 * Errors: throws `TypeError` when `id` is not a string, is longer than 96 characters or fails the
 * grammar; when `parsers` is given and is not an object; when `input` is given and is not a parser;
 * or when `outputs` is given and is not a plain object of parsers. The message names the fault and
 * never quotes the refused value.
 *
 * Pure: no I/O, no registry read. The function does not check that the point exists; the runtime
 * answers an unknown point `unresolved` at mount.
 */
export function defineExtensionPoint(id, parsers) {
    if (typeof id !== 'string' || id.length > REF_ID_MAX_LENGTH || !REF_ID.test(id)) {
        throw new TypeError('defineExtensionPoint: the id must be <owner>/<name>.vN, at most 96 characters');
    }
    if (parsers === undefined)
        return Object.freeze({ id });
    if (typeof parsers !== 'object' || parsers === null) {
        throw new TypeError('defineExtensionPoint: parsers must be an object');
    }
    const ref = { id };
    if (parsers.input !== undefined) {
        if (!isParser(parsers.input))
            throw new TypeError('defineExtensionPoint: input must be a parser with safeParse');
        ref.input = parsers.input;
    }
    if (parsers.outputs !== undefined) {
        const outputs = parsers.outputs;
        if (typeof outputs !== 'object' || outputs === null || Array.isArray(outputs)) {
            throw new TypeError('defineExtensionPoint: outputs must be an object of parsers');
        }
        const copy = {};
        for (const name of Object.keys(outputs)) {
            const parser = outputs[name];
            if (!isParser(parser))
                throw new TypeError('defineExtensionPoint: each output must be a parser with safeParse');
            // defineProperty, so an own `__proto__` key stays a key and never sets the prototype.
            Object.defineProperty(copy, name, { value: parser, enumerable: true });
        }
        ref.outputs = Object.freeze(copy);
    }
    return Object.freeze(ref);
}
//# sourceMappingURL=extension-point-ref.js.map